import { AnimatePresence, motion } from "framer-motion";
import { Flame, Star, Zap, GraduationCap, Trophy, Gamepad2, X, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const BADGE_ICONS = { first_step: Flame, perfect: Star, consistent: Zap, graduate: GraduationCap, elite: Trophy, game_master: Gamepad2 };
const COACH_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/assets/covers/coach-cheer.png`;

export const CoachCelebration = ({ open, onClose, title, subtitle, badges = [], newBadges = [], courseBadgeUrl = "" }) => {
  const { t } = useLang();
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-[#0B1B3A]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coach-celebration" onClick={onClose}>
          <motion.div
            initial={{ scale: 0.7, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-[32px] p-7 sm:p-9 text-center shadow-2xl overflow-hidden"
          >
            <button data-testid="coach-close" onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#EEF4FF] text-[#0B1B3A] flex items-center justify-center hover:bg-[#DBEAFE] transition-colors duration-200">
              <X className="w-4 h-4" />
            </button>
            {[...Array(10)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 380, opacity: [0, 1, 0], rotate: 360 }}
                transition={{ duration: 2.2 + (i % 4) * 0.4, delay: i * 0.12, repeat: Infinity }}
                className="absolute top-0 w-2.5 h-2.5 rounded-sm pointer-events-none"
                style={{ left: `${8 + i * 9}%`, background: i % 2 ? "#1D4ED8" : "#93C5FD" }}
              />
            ))}
            <motion.img
              src={COACH_IMG}
              alt="Coach"
              animate={{ rotate: [0, -4, 4, -2, 0], y: [0, -6, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 1.2 }}
              className="w-44 h-44 sm:w-52 sm:h-52 object-contain mx-auto"
              data-testid="coach-image"
            />
            <span className="badge-lime mt-2 inline-flex"><Sparkles className="w-3.5 h-3.5 mr-1" /> {t("coach.badge")}</span>
            <h3 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A] mt-3" data-testid="coach-title">{title || t("coach.title")}</h3>
            {subtitle && <p className="text-sm text-[#475569] mt-2" data-testid="coach-subtitle">{subtitle}</p>}
            {courseBadgeUrl && (
              <motion.img initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.4, type: "spring" }} src={courseBadgeUrl} alt="" className="w-36 h-36 object-contain mx-auto mt-4 drop-shadow-xl" data-testid="coach-course-badge" />
            )}
            {newBadges.length > 0 && (
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1D4ED8] mt-5" data-testid="coach-new-badge">
                {t("coach.newBadge")}: {newBadges.map((b) => t(`badge.${b}`)).join(", ")}
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-2.5 mt-5" data-testid="coach-badges">
              {badges.length === 0 && <p className="text-xs text-[#94A3B8]">{t("coach.noBadges")}</p>}
              {badges.map((key, i) => {
                const Icon = BADGE_ICONS[key] || Star;
                const fresh = newBadges.includes(key);
                return (
                  <motion.span
                    key={key}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 + i * 0.1, type: "spring" }}
                    data-testid={`coach-badge-${key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${fresh ? "bg-[#1D4ED8] text-white ring-4 ring-[#BFDBFE]" : "bg-[#EEF4FF] text-[#0B1B3A]"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {t(`badge.${key}`)}
                  </motion.span>
                );
              })}
            </div>
            <button data-testid="coach-continue" onClick={onClose} className="btn-blue mt-7 w-full justify-center">{t("coach.continue")}</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CoachCelebration;
